import type { CurrentUser } from '../types/models';
import type { UserEnd } from '../enums';

/**
 * 会话与令牌存储 — 按端命名空间隔离（admin 与 bpo 独立鉴权域，不共享登录态）。
 * D-1（契约 §2）：会话＝**24 小时绝对上限，自登录起算、不滑动续期**。本模块只做**存储**：
 * 截止时刻一律把后端给出的绝对时刻（登录响应 `expiresAt`）原样落盘，⛔ 不按分钟数二次换算。
 * 令牌仅存 sessionStorage，降低 XSS 持久化风险。
 */

const END: UserEnd = (import.meta.env.VITE_APP_END as UserEnd) || 'ADMIN';
const NS = `dh:${END.toLowerCase()}`;

const K_TOKEN = `${NS}:token`;
const K_REFRESH = `${NS}:refresh`;
const K_USER = `${NS}:user`;
const K_EXPIRES = `${NS}:expires`;
const K_REMEMBER = `${END.toLowerCase()}:remember-account`;

function safeGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string, persist = false): void {
  try {
    if (persist) window.localStorage.setItem(key, value);
    else window.sessionStorage.setItem(key, value);
  } catch {
    /* 存储不可用时静默降级为内存态 */
  }
}

function safeRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function getAccessToken(): string | null {
  const expires = Number(safeGet(K_EXPIRES) || 0);
  if (expires && expires < Date.now()) {
    clearSession();
    return null;
  }
  return safeGet(K_TOKEN);
}

export function getRefreshToken(): string | null {
  return safeGet(K_REFRESH);
}

export function saveSession(payload: {
  accessToken: string;
  refreshToken?: string;
  /**
   * 后端给出的会话**绝对截止时刻**（epoch ms；来源＝登录响应 `expiresAt`，缺省时由令牌 `exp` 归一）。
   * ⛔ 不接受「剩余分钟数」：`Date.now() + minutes` 的二次换算正是 D-1 要消灭的「前端自造剩余时长」。
   */
  expiresAtMs: number;
  user: CurrentUser;
}): void {
  safeSet(K_TOKEN, payload.accessToken);
  if (payload.refreshToken) safeSet(K_REFRESH, payload.refreshToken);
  // 原样落盘后端时刻：倒计时（AdminLayout）与登出判定（router 守卫）同源同值
  safeSet(K_EXPIRES, String(payload.expiresAtMs));
  safeSet(K_USER, JSON.stringify(payload.user));
}

export function getSessionUser(): CurrentUser | null {
  const raw = safeGet(K_USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CurrentUser;
  } catch {
    return null;
  }
}

/** §6.2-7 刷新后重建权限态时只更新身份快照，不动令牌与会话时限 */
export function saveSessionUser(user: CurrentUser): void {
  safeSet(K_USER, JSON.stringify(user));
}

export function sessionExpiresAt(): number {
  return Number(safeGet(K_EXPIRES) || 0);
}

export function clearSession(): void {
  [K_TOKEN, K_REFRESH, K_USER, K_EXPIRES].forEach(safeRemove);
}

/** 记住用户名（跨会话，仅账号不含密码） */
export function getRememberedAccount(): string {
  try {
    return window.localStorage.getItem(K_REMEMBER) || '';
  } catch {
    return '';
  }
}

export function setRememberedAccount(account: string): void {
  try {
    if (account) window.localStorage.setItem(K_REMEMBER, account);
    else window.localStorage.removeItem(K_REMEMBER);
  } catch {
    /* ignore */
  }
}

/**
 * 归一「后端声明的时刻」⇒ epoch ms：ISO 8601 字符串 / epoch 秒 / epoch 毫秒（无法识别＝0）。
 * D-3（契约 §2）：5173 只做归一与展示，⛔ 不自造锁定窗口。
 */
export function toEpochMs(value: unknown): number {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return 0;
    return value < 1e12 ? Math.round(value * 1000) : Math.round(value);
  }
  if (typeof value === 'string' && value) {
    const parsed = new Date(value).getTime();
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 0;
}

/**
 * 登录失败锁定（后端口径＝连错 5 次锁 30min；权威时刻＝423 响应体 `data.lockedUntil`，t80）。
 * `ACCOUNT_LOCK_MINUTES` **只是「后端未声明确定时刻」时的名义兜底**（今日 mock 桩档即此形状），
 * ⛔ 不是权威窗口；到点判定与倒计时一律用已归一的后端时刻。
 */
const K_FAIL = `${NS}:fail`;
export const ACCOUNT_LOCK_MINUTES = 30;

export function recordLoginFailure(lockedUntilAt?: unknown): { count: number; lockedUntil: number } {
  const count = Number(safeGet(K_FAIL) || 0) + 1;
  safeSet(K_FAIL, String(count));
  const declared = toEpochMs(lockedUntilAt);
  const stamp = Date.now() + ACCOUNT_LOCK_MINUTES * 60 * 1000;
  const lockedUntil = declared > 0 ? declared : count >= 5 ? stamp : 0;
  if (lockedUntil) safeSet(`${NS}:locked`, String(lockedUntil));
  return { count, lockedUntil };
}

export function resetLoginFailure(): void {
  safeRemove(K_FAIL);
  safeRemove(`${NS}:locked`);
}

export function lockedUntil(): number {
  const value = Number(safeGet(`${NS}:locked`) || 0);
  return value > Date.now() ? value : 0;
}
